// Utilitários da aba Acordos e Recebimentos da Análise de Atendimento (tela). A planilha não usa este arquivo.
import { numerosDaPessoa, paraLista, passaNoFiltro } from "./analiseUtils";

// Medidas somáveis que o backend manda por (negociador, coordenador, cliente, mês) (AnaliseAcordosService.MEDIDAS)
export const MEDIDAS_ACORDOS = [
    "acordos", "fichasConvertidas", "valorAcordos", "valorOriginal", "desconto", "acordosAVista", "acordosQuitados", "acordosCancelados",
    "valorCancelados", "pagamentos", "recebidoAcordos", "recebidoAvulso", "parcelasVencidas", "valorVencidas",
    "vencidasPagas", "valorVencidasPagas", "parcelasAVencer", "valorAVencer",
];

const formatoMoeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const formatoMoedaCentavos = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const formatoCompacto = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

/** "R$ 12.346" (análise: sem centavos; a planilha leva os centavos). */
export const formatarMoeda = (valor) => (valor === null || valor === undefined ? "-" : formatoMoeda.format(valor));
export const formatarMoedaCentavos = (valor) => (valor === null || valor === undefined ? "-" : formatoMoedaCentavos.format(valor));
/** "R$ 1,2 mi" / "R$ 350 mil": rótulos dos gráficos. */
export const formatarMoedaCurta = (valor) => `R$ ${formatoCompacto.format(valor || 0)}`;

const vazio = () => Object.fromEntries(MEDIDAS_ACORDOS.map(m => [m, 0]));

const somarEm = (acc, linha) => {
    MEDIDAS_ACORDOS.forEach(m => { acc[m] += Number(linha[m]) || 0; });
    return acc;
};

export const somarLinhas = (linhas) => linhas.reduce(somarEm, vazio());

/** Filtros só da tela (cliente, negociador e coordenador; cada um com um ou vários valores). */
export const filtrarLinhas = (linhas, cliente, negociador, coordenador = null) => (linhas || [])
    .filter(l => passaNoFiltro(cliente, l.cliente))
    .filter(l => passaNoFiltro(negociador, l.negociador))
    .filter(l => passaNoFiltro(coordenador, l.coordenador));

/** Totais por negociador, coordenador ou cliente: [{ nome, ...medidas }]. */
export function agruparPor(linhas, campo) {
    const grupos = new Map();
    linhas.forEach(l => {
        const nome = l[campo];
        if (!grupos.has(nome)) grupos.set(nome, { nome, ...vazio() });
        somarEm(grupos.get(nome), l);
    });
    return [...grupos.values()];
}

/** Totais de cada mês, na ordem do período (meses sem nada vêm zerados). */
export const totaisPorMes = (linhas, meses) => meses.map((rotulo, idx) => ({
    nome: rotulo,
    mes: idx,
    ...somarLinhas(linhas.filter(l => l.mes === idx)),
}));

const divisao = (a, b) => (b ? a / b : null);

/**
 * Indicadores derivados de um total.
 * - ticket médio = valor negociado ÷ acordos;
 * - desconto = desconto ÷ (valor negociado + desconto), o valor antes do desconto;
 * - quebra = acordos do período já cancelados ÷ acordos do período;
 * - adimplência = valor pago das parcelas vencidas no período ÷ valor dessas parcelas;
 * - conversão = fichas acionadas que tiveram acordo de quem as acionou ÷ fichas acionadas (nunca passa de 100%).
 */
export function derivados(total, fichasAcionadas = null) {
    return {
        recebido: (total.recebidoAcordos || 0) + (total.recebidoAvulso || 0),
        ticketMedio: divisao(total.valorAcordos, total.acordos),
        descontoPct: divisao(total.desconto, (total.valorAcordos || 0) + (total.desconto || 0)),
        quebra: divisao(total.acordosCancelados, total.acordos),
        adimplencia: divisao(total.valorVencidasPagas, total.valorVencidas),
        adimplenciaQtd: divisao(total.vencidasPagas, total.parcelasVencidas),
        fichasAcionadas,
        conversao: fichasAcionadas ? (total.fichasConvertidas || 0) / fichasAcionadas : null,
    };
}

const somar = (lista) => lista.reduce((acc, v) => acc + (v || 0), 0);

/**
 * Fichas acionadas (da análise de atendimento, mesmos período e carteira) para a conversão.
 * dimensao: "total" | "negociador" | "coordenador" | "cliente" | "mes"; chave: nome, cliente ou índice do mês.
 * Com negociadores (ou coordenadores) escolhidos, os números são a soma dos deles; a outra dimensão não tem a conta
 * separada (null). Cliente: um ou vários.
 */
export function fichasAcionadas(atendimento, { dimensao, chave, cliente, negociador, coordenador }) {
    if (!atendimento) return null;
    const qtdMeses = atendimento.meses?.length || 0;
    const blocoDe = (lista, nome) => (atendimento[lista] || []).find(b => b.nome === nome);
    const totaisClientes = (atendimento.clientes || []).filter(c => passaNoFiltro(cliente, c.cliente));
    const negociadores = paraLista(negociador);
    const coordenadores = paraLista(coordenador);

    if (negociadores.length || coordenadores.length) {
        const propria = negociadores.length ? "negociador" : "coordenador";
        if (dimensao === (propria === "negociador" ? "coordenador" : "negociador")) return null;
        const nomes = dimensao === propria ? [chave] : (negociadores.length ? negociadores : coordenadores);
        const numeros = nomes
            .map(nome => blocoDe(propria === "negociador" ? "negociadores" : "coordenadores", nome))
            .filter(Boolean)
            .map(bloco => numerosDaPessoa(bloco, dimensao === "cliente" ? chave : cliente, qtdMeses));
        return dimensao === "mes" ? somar(numeros.map(n => n.fichasMes[chave] || 0)) : somar(numeros.map(n => n.fichas));
    }
    switch (dimensao) {
        case "negociador":
        case "coordenador": {
            const bloco = blocoDe(dimensao === "negociador" ? "negociadores" : "coordenadores", chave);
            return bloco ? numerosDaPessoa(bloco, cliente, qtdMeses).fichas : 0;
        }
        case "cliente": {
            const total = (atendimento.clientes || []).find(c => c.cliente === chave);
            return total ? total.fichasPeriodo : 0;
        }
        case "mes":
            return somar(totaisClientes.map(c => (c.fichasMes || [])[chave]));
        default:
            return somar(totaisClientes.map(c => c.fichasPeriodo));
    }
}

/**
 * Fichas acionadas de uma combinação de negociador ou coordenador, cliente e mês (índice), com os filtros da tela
 * valendo para o que a combinação não tem. Negociador e coordenador juntos: não há essa conta (null). Com o mês, as
 * fichas acionadas naquele mês; sem ele, as do período (cada ficha uma vez).
 */
export function fichasDaCombinacao(atendimento, combinacao, filtros = {}) {
    if (!atendimento) return null;
    const negociadores = paraLista(combinacao.negociador ?? filtros.negociador);
    const coordenadores = paraLista(combinacao.coordenador ?? filtros.coordenador);
    const cliente = combinacao.cliente ?? filtros.cliente ?? null;
    const mes = combinacao.mes ?? null;
    if (negociadores.length && coordenadores.length) return null;
    if (negociadores.length || coordenadores.length) {
        const nomes = negociadores.length ? negociadores : coordenadores;
        const linhas = (atendimento[negociadores.length ? "negociadores" : "coordenadores"] || [])
            .filter(b => nomes.includes(b.nome))
            .flatMap(b => b.linhas)
            .filter(l => passaNoFiltro(cliente, l.cliente));
        return mes !== null ? somar(linhas.map(l => (l.quantidades || [])[mes])) : somar(linhas.map(l => l.fichasPeriodo));
    }
    const totais = (atendimento.clientes || []).filter(c => passaNoFiltro(cliente, c.cliente));
    return mes !== null ? somar(totais.map(c => (c.fichasMes || [])[mes])) : somar(totais.map(c => c.fichasPeriodo));
}

/** Negociadores presentes nos dados (para o filtro), em ordem alfabética. */
export const negociadoresDosDados = (dados) => [...new Set((dados?.linhas || []).map(l => l.negociador))]
    .sort((a, b) => a.localeCompare(b));

// Mesmo par azul/laranja das posições 0 e 1 da paleta validada de analiseUtils (CORES_PESSOAS), aqui como medidas
const CORES = {
    negociado: { light: "#2a78d6", dark: "#3987e5" },
    recebido: { light: "#eb6834", dark: "#d95926" },
};
export const corAcordos = (medida, darkMode) => CORES[medida][darkMode ? "dark" : "light"];

// Estados (sempre com ícone e texto ao lado, nunca só a cor)
const ESTADOS = {
    bom: { light: "#006300", dark: "#0ca30c" },
    andamento: { light: "#898781", dark: "#b5b4ab" },
    ruim: { light: "#d03b3b", dark: "#e66767" },
};
export const corEstado = (estado, darkMode) => ESTADOS[estado][darkMode ? "dark" : "light"];
